import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

import MobileAppBar from '@/components/molecules/MobileAppBar';
import InfoModal from '@/components/molecules/InfoModal';
import BudgetCreationSteps from '@/components/molecules/budget/BudgetCreationSteps';
import BudgetCreationStepsMobile from '@/components/molecules/budget/BudgetCreationStepsMobile';
import BudgetMemberCalculationsFooter from '@/components/molecules/budget/BudgetMemberCalculationsFooter';
import BudgetCreationForm, { BudgetCreationFormHandle } from '@/components/organisms/BudgetCreationForm';
import { BUDGET_CREATION_HEADER } from '@/components/organisms/budget/budgetCreation.constants';
import { Button } from '@/components/ui/button';
import gearIcon from '@/assets/icons/gear.svg';
import profileDiscardIcon from '@/assets/icons/profile-discard.svg';
import { budgetService } from '@/services/budget.service';
import {
  getInvitableMembers,
  getOwnerMember,
  toApiMemberType
} from '@/components/organisms/budget/form-steps/budgetStep.schema';
import { hslToHex } from '@/utils/ColorConverter';
import { useNavigationGuard } from '@/hooks/useNavigationGuard';
import { BudgetMemberCalculations } from '@/utils/budgetCalculations';

const AddPage = () => {
  const navigate = useNavigate();
  const formRef = useRef<BudgetCreationFormHandle>(null);
  const [activeStep, setActiveStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [memberCalculations, setMemberCalculations] = useState<BudgetMemberCalculations | null>(null);

  const { isDiscardModalOpen, handleCancel, handleDiscardChanges, handleDiscardModalClose, allowNavigation } =
    useNavigationGuard({
      isDirty: isFormDirty,
      fallbackPath: '/budgets'
    });

  const handleFormSubmit = async (data: any) => {
    try {
      setIsSubmitting(true);

      const colorHex = hslToHex(data.budgetColor);

      const membersToInvite = getInvitableMembers(data.members ?? []);
      const ownerMember = getOwnerMember(data.members ?? []);

      const budgetData = {
        name: data.budgetName,
        type: data.budgetType,
        icon_id: data.budgetIcon,
        color_hex: colorHex,
        description: data.description || '',
        start_date: data.startDate.toISOString().split('T')[0],
        end_date: data.endDate.toISOString().split('T')[0],
        period: data.period,
        currency: data.currency,
        is_auto_renewal: data.autoRenewal,
        bmev: data.bmev ? Number(data.bmev) : 0,
        bmev_alert_threshold: data.bmevThreshold,
        btrt: data.btrt ? Number(data.btrt) : 0,
        btrt_alert_threshold: data.btrtThreshold,
        owner: ownerMember?.type
          ? {
              type: toApiMemberType(ownerMember.type),
              ore_hours: Number(ownerMember.oreHours) || 0,
              hourly_rate: Number(ownerMember.hourlyRate) || 0
            }
          : undefined,
        invite_members:
          membersToInvite.length > 0
            ? membersToInvite.map((m) => ({
                email: m.email,
                role: (m.role === 'validation' ? 'VALIDATOR' : 'STAFF') as 'VALIDATOR' | 'STAFF',
                type: toApiMemberType(m.type),
                ore_hours: Number(m.oreHours) || 0
              }))
            : undefined
      };

      const response: any = await budgetService.createBudget(budgetData);
      const budgetId = response?.data?._id ?? response?.data?.id;

      toast.success('Budget created successfully!');
      allowNavigation();
      navigate('/budgets/' + budgetId);
    } catch (error: any) {
      console.error('Error creating budget:', error);
      toast.error(error?.message ?? 'Failed to create budget. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLeftButtonClick = () => {
    if (activeStep === 1) {
      handleCancel();
      return;
    }
    setActiveStep(1);
  };

  const handleRightButtonClick = async () => {
    if (activeStep === 1) {
      const isValid = await formRef.current?.validateStep(1);
      if (isValid) {
        setActiveStep(2);
      }
      return;
    }

    const isValid = await formRef.current?.validateStep(2);
    if (isValid) {
      formRef.current?.submit();
    }
  };

  const isLastStep = activeStep === 2;

  return (
    <>
      <MobileAppBar title="Budget Creation" onRightIconClick={() => {}} rightIcon={gearIcon} />

      <div className="flex-1 bg-[#FBFBFF] p-4 md:bg-white rounded-[16px]">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-3 md:mb-0">
          <h2 className="text-center font-bold mb-1 md:text-left md:text-[24px] md:mb-6">
            {BUDGET_CREATION_HEADER[activeStep]}
          </h2>

          <BudgetCreationSteps activeStep={activeStep} />
        </div>

        <div className="md:hidden mb-6">
          <BudgetCreationStepsMobile activeStep={activeStep} />
        </div>

        <BudgetCreationForm
          ref={formRef}
          activeStep={activeStep}
          onSubmit={handleFormSubmit}
          onFormStateChange={({ isDirty }) => setIsFormDirty(isDirty)}
          onMemberCalculationsChange={setMemberCalculations}
        />
      </div>

      <footer className="flex flex-col md:flex-row items-end justify-between gap-4 px-4 py-7 bg-gradient-to-b from-[#F0EFFD]/0 from-0% to-[#F0EFFD] to-[17.74%] md:bg-white md:bg-none md:from-transparent md:to-transparent">
        <div className="flex-1 w-full md:w-auto min-w-0">
          {activeStep === 2 && <BudgetMemberCalculationsFooter calculations={memberCalculations} />}
        </div>
        <div className="w-full md:w-auto flex justify-end gap-4 shrink-0 mt-2 md:mt-0">
          <Button
            className="hidden md:block rounded-[24px] text-base font-semibold px-6 h-[44px] text-[16px] w-full md:w-fit text-[#E8507E]"
            variant={'text'}
            onClick={handleLeftButtonClick}
            disabled={isSubmitting}
            type="button"
          >
            {activeStep === 1 ? 'CANCEL' : 'PREVIOUS'}
          </Button>
          <Button
            className="rounded-[24px] text-base font-semibold px-6 h-[44px] text-[16px] w-full md:w-fit"
            onClick={handleRightButtonClick}
            disabled={isSubmitting}
            type="button"
          >
            {isSubmitting ? 'CREATING...' : isLastStep ? 'CREATE' : 'NEXT'}
          </Button>
        </div>
      </footer>

      {isDiscardModalOpen && (
        <InfoModal
          isOpen={isDiscardModalOpen}
          variant="confirm"
          title="Are you sure you want to discard changes?"
          message="Unsaved changes will not be saved into your budget"
          buttonText="Discard"
          onButtonClick={handleDiscardChanges}
          icon={profileDiscardIcon}
          onClose={handleDiscardModalClose}
        />
      )}
    </>
  );
};

export default AddPage;
